import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";

import type { DocumentsItemDto } from "@/api/types";
import documentEmpty from "@/assets/images/document-empty.svg";
import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ContextMenu, type ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { InfoCircleIcon } from "@/components/Icons/InfoCircleIcon";
import { MoveIcon } from "@/components/Icons/MoveIcon";
import { PlusIcon } from "@/components/Icons/PlusIcon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Pagination } from "@/components/Pagination/Pagination";
import { ShareButton } from "@/components/ShareButton/ShareButton";
import { Button } from "@/design-system/Button";
import { Card } from "@/design-system/Card";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Inline } from "@/design-system/Inline";
import { Link } from "@/design-system/Link";
import { Stack } from "@/design-system/Stack";
import { createDashboardColumnHelper } from "@/design-system/Table/helpers";
import { TABLE_COLUMN_SIZES } from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import { Tooltip } from "@/design-system/Tooltip";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import { useSlug } from "@/hooks/useSlug";
import { DocumentsFileIcon } from "@/modules/documents/components/DocumentsFileIcon";
import { DocumentsMoveItemModal } from "@/modules/documents/components/DocumentsMoveItemModal";
import { documentMutations } from "@/queries/documents/mutations";
import { useDocumentQueries } from "@/queries/documents/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

const ROOT_FOLDER_ID = "root";
const PAGE_SIZE = 10;

export function DocumentsListPage(): React.ReactNode {
  const projectId = useProjectId();
  const [page, setPage] = useState(0);
  const [moveItem, setMoveItem] = useState<DocumentsItemDto | undefined>(undefined);

  const { componentProps, openDeleteModal } = useDeleteModal<FolderItemDeleteParam>("document-delete-modal");
  const slug = useSlug();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { folderId } = useParams<{
    folderId: string;
  }>();

  const documentQueries = useDocumentQueries();
  const {
    data: documentsData,
    isPending: isPendingDocumentList,
    error: errorDocumentsData,
  } = useQuery({
    ...documentQueries.listDocuments({
      folderId: folderId!,
      page,
    }),
    enabled: Boolean(folderId),
  });
  const deleteFolderMutation = documentMutations.useDeleteFolder();
  const deleteFileMutation = documentMutations.useDeleteFile();
  const updateFileViewCountMutation = documentMutations.useUpdateFileViewCount();

  const handleDeleteItem = async ({ id, isFolder }: { id: string; isFolder: boolean }) => {
    if (!folderId) {
      return;
    }
    if (isFolder) {
      await deleteFolderMutation.mutateAsync({
        parentFolderId: folderId,
        folderId: id,
      });

      return;
    }
    await deleteFileMutation.mutateAsync({
      folderId,
      fileId: id,
    });
  };
  const handleOpenDocument = useCallback(
    (id: string) => {
      if (!folderId) {
        return;
      }
      void updateFileViewCountMutation.mutateAsync({
        folderId: folderId,
        fileId: id,
      });
    },
    [folderId, updateFileViewCountMutation],
  );
  const anyHasViewCount = documentsData?.items?.some((doc) => doc.viewCount != null);
  const columns = useMemo(() => {
    const helper = createDashboardColumnHelper<Omit<DocumentsItemDto, "parent">>();

    return [
      helper.accessor("name", {
        header: t("page.document.list.columns.name.title"),
        cell: (cell) => {
          const content = (
            <span className="inline-flex items-center gap-space-sm">
              <DocumentsFileIcon type={cell.row.original.type} />
              {cell.getValue()}
            </span>
          );

          return content;
        },
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
        meta: {
          shouldSimulateRowClick: true,
          renderTruncatedTooltip: (cell) => cell.getValue(),
        },
      }),
      helper.accessor("createdAt", {
        header: t("page.document.list.columns.created.title"),
        cell: (cell) => <FormattedDate date={cell.getValue()} format="dateRelative" />,
        minSize: TABLE_COLUMN_SIZES.DATE.MIN,
        size: TABLE_COLUMN_SIZES.DATE.DEFAULT,
      }),
      helper.accessor("document", {
        header: t("page.document.list.columns.file.title"),
        cell: (cell) => {
          const value = cell.getValue();

          return value ? (
            <Link
              data-testid={`file_${cell.row.original.name}`}
              href={value?.url}
              external
              target="_blank"
              onClick={() => handleOpenDocument(cell.row.original.id)}
              underline="hover"
            >
              <span className="break-all">{value?.fileName || value.url}</span>
            </Link>
          ) : (
            <span>-</span>
          );
        },
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      ...(anyHasViewCount
        ? [
            helper.accessor("viewCount", {
              header: () => (
                <Tooltip tooltip={t("common.tooltip.view-count-disclaimer")}>
                  <Inline gap="xs" align="center">
                    {t("page.document.list.columns.view.title")}
                    <InfoCircleIcon size={16} tone="secondary" />
                  </Inline>
                </Tooltip>
              ),
              cell: (cell) => {
                return <span>{cell.row.original.type !== "folder" ? cell.getValue() : "-"}</span>;
              },
              minSize: TABLE_COLUMN_SIZES.COUNT.MIN,
              size: TABLE_COLUMN_SIZES.COUNT.DEFAULT,
            }),
          ]
        : []),
      helper.display({
        id: "share",
        cell: (cell) => {
          const canBeShared = cell.row.original.type !== "folder" && !!folderId;

          if (!canBeShared) {
            return null;
          }

          return (
            <ShareButton
              entityId={cell.row.original.id}
              entityType="document"
              otherIds={{
                folderId,
              }}
            />
          );
        },
        enableResizing: false,
        minSize: 72,
        size: 72,
      }),
      helper.accessor("id", {
        header: "",
        cell: (cell) => {
          const id = cell.getValue();
          const menuActions: ContextMenuItem[] = [
            {
              labels: {
                default: t("page.document.list.action.edit"),
                disabled: t("model.document.folder.action.edit.not-allowed"),
              },
              "data-testid": "context-menu-edit-btn",
              value: "edit",
              icon: <Edit05Icon />,
              callbackFn: () => {
                if (cell.row.original.type === "folder") {
                  void navigate(
                    routes.documents.folderEdit({
                      slug,
                      folderId: id,
                    }),
                  );
                } else {
                  void navigate(
                    routes.documents.fileEdit({
                      slug,
                      folderId: folderId!,
                      fileId: id,
                    }),
                  );
                }
              },
              isDisabled: !cell.row.original.canEdit,
            },
            {
              labels: {
                default: t("page.document.list.action.move"),
                disabled: t("model.document.folder.action.move.not-allowed"),
              },
              value: "move",
              icon: <MoveIcon />,
              callbackFn: () => setMoveItem(cell.row.original),
              isDisabled: !cell.row.original.canMove,
            },
            {
              labels: {
                default: t("common.action.delete"),
                disabled:
                  cell.row.original.type === "folder"
                    ? t("model.document.folder.action.delete.not-allowed")
                    : t("model.document.file.action.delete.not-allowed"),
              },
              "data-testid": "context-menu-delete-btn",
              value: "delete",
              icon: <Trash02Icon />,
              callbackFn: () => {
                openDeleteModal({
                  id,
                  isFolder: cell.row.original.type === "folder",
                });
              },
              isDisabled: !cell.row.original.canDelete,
            },
          ];

          return <ContextMenu items={menuActions} />;
        },
        size: TABLE_COLUMN_SIZES.ACTION.DEFAULT,
        meta: {
          isAction: true,
        },
      }),
    ];
  }, [t, slug, handleOpenDocument, navigate, folderId, openDeleteModal, anyHasViewCount]);

  const folderTable = useResizableTable(TABLE_KEYS.DOCUMENTS_FOLDERS, {
    columns,
    data: documentsData?.items ?? [],
    initialState: {
      columnVisibility: {
        createdAt: isDesktop,
      },
    },
    enableSorting: false,
  });

  const canAddItems = documentsData?.canAddNew || false;
  const totalPages = documentsData ? Math.ceil(documentsData.total / PAGE_SIZE) : 0;
  const listDocuments = documentsData?.items || [];

  if (!folderId) {
    return <ErrorPage status={404} />;
  }

  if (errorDocumentsData) {
    return <ErrorPage error={errorDocumentsData} />;
  }

  if (isPendingDocumentList) {
    return <FullSizeLoader withPadding />;
  }

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={documentsData.name}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("model.document.breadcrumbs.root"),
              id: ROOT_FOLDER_ID,
            },
            ...documentsData.ancestors,
            {
              name: documentsData.name,
              id: folderId,
            },
          ].map(({ name, id: currFolderId }) => ({
            to:
              currFolderId === ROOT_FOLDER_ID
                ? routes.documents.rootList({
                    slug,
                  })
                : routes.documents.documentsList({
                    slug,
                    folderId: currFolderId,
                  }),
            name,
          }))}
        />
      }
      actions={
        <>
          <AskNobiButton
            defaultFilters={{ projectIds: [projectId] }}
            context={{ scope: "project", entity: "documents", page: "list", entityId: folderId }}
            suggestions={[
              t("component.ask-nobi-chat-drawer.documents.suggestion.1"),
              t("component.ask-nobi-chat-drawer.documents.suggestion.2"),
            ]}
          />
          {canAddItems && (
            <>
              <Button
                type="link"
                data-testid="documents-folder-create"
                variant="soft"
                icon={<PlusIcon />}
                href={routes.documents.folderCreate({
                  slug,
                  folderId,
                })}
              >
                {t("page.document.list.action.new-folder")}
              </Button>
              <Button
                type="link"
                data-testid="documents-file-upload"
                href={routes.documents.fileCreate({
                  slug,
                  folderId,
                })}
              >
                {t("page.document.list.action.new-item")}
              </Button>
            </>
          )}
        </>
      }
    >
      <Stack gap="md">
        {listDocuments.length > 0 ? (
          <Table
            table={folderTable}
            getRowHref={(row) =>
              row.original.type === "folder"
                ? routes.documents.documentsList({
                    slug,
                    folderId: row.original.id,
                  })
                : ""
            }
          >
            <Pagination count={totalPages} currentIndex={page} onChange={setPage} />
          </Table>
        ) : (
          <Card as="section" density="flush" clip>
            <section className="flex flex-col items-center justify-center py-page-bottom text-center">
              <img
                className="mb-space-lg w-no-data-illustration max-w-full"
                src={documentEmpty}
                alt=""
                role="presentation"
              />
              <h2 className="text-body-emphasis">{t("page.document.root.list.empty.title")}</h2>
              <p className="text-text-secondary">{t("page.document.root.list.empty.description")}</p>
            </section>
          </Card>
        )}
      </Stack>
      <DocumentsMoveItemModal
        folderId={folderId}
        item={moveItem}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setMoveItem(undefined);
          }
        }}
        isOpened={Boolean(moveItem)}
      />
      <DeleteModal<FolderItemDeleteParam>
        title={
          componentProps.modalContext?.isFolder
            ? t("model.document.folder.action.delete.confirmation")
            : t("model.document.file.action.delete.confirmation")
        }
        description={
          componentProps.modalContext?.isFolder
            ? t("model.document.folder.action.delete.description")
            : t("model.document.file.action.delete.description")
        }
        onDelete={handleDeleteItem}
        {...componentProps}
      />
    </DocumentPage>
  );
}
type FolderItemDeleteParam = {
  id: string;
  isFolder: boolean;
};
